import { useState } from "react";
import { parseUnits } from "viem";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useTokenMetadata } from "../TokenVisibility";

const pad = (n) => String(n).padStart(2, "0");
const toLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const inp = "w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-lime/60 outline-none";

const Choice = ({ value, set, options, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map(([v, l, hint]) => (
      <button type="button" key={v} onClick={() => set(v)} data-testid={`${testid}-${v}`} title={hint}
        className={`rounded-lg border px-3 py-1.5 text-xs text-left ${value === v ? "border-lime bg-lime/10 text-lime" : "border-white/10 text-slate-300 hover:border-lime/40"}`}>
        {l}{hint && <span className="block text-[10px] text-slate-500">{hint}</span>}
      </button>
    ))}
  </div>
);

const F = ({ label, children }) => <label className="block space-y-1.5"><span className="text-xs text-slate-400">{label}</span>{children}</label>;

export const CompetitionForm = ({ token, onCreated }) => {
  const meta = useTokenMetadata(token);
  const now = new Date(Date.now() + 15 * 60000);
  const [f, setF] = useState({ title: "Trading competition", start: toLocal(now), end: toLocal(new Date(now.getTime() + 7 * 86400000)), winners: 3,
    metric: "buy_volume", split_mode: "top_heavy", pcts: ["50", "30", "20"], leftover: "refund", reward: "", min_volume_usd: "0" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const setWinners = (n) => setF((p) => ({ ...p, winners: n, pcts: Array.from({ length: n }, (_, i) => (i === 0 ? (100 - Math.floor(10000 / n) / 100 * (n - 1)).toFixed(2) : (Math.floor(10000 / n) / 100).toFixed(2))) }));
  const sum = f.pcts.reduce((a, b) => a + Number(b || 0), 0);
  const submit = async () => {
    if (!meta) return toast.error("Token metadata not available");
    let reward;
    try { reward = parseUnits(f.reward || "0", meta.decimals); } catch { return toast.error("Enter a valid reward amount"); }
    if (reward <= 0n) return toast.error("Enter the reward amount for the pot");
    if (f.split_mode === "custom" && Math.abs(sum - 100) > 0.001) return toast.error("Custom split must add up to 100%");
    setBusy(true);
    try {
      await api.post("/competitions", { token, title: f.title, start_ts: Math.floor(new Date(f.start).getTime() / 1000), end_ts: Math.floor(new Date(f.end).getTime() / 1000),
        winners: f.winners, metric: f.metric, split_mode: f.split_mode, split_pcts: f.split_mode === "custom" ? f.pcts.map(Number) : null,
        leftover: f.leftover, reward_amount: reward.toString(), min_volume_usd: Number(f.min_volume_usd || 0) });
      toast.success("Competition created — now deploy its reward pot");
      onCreated();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="p-5 grid md:grid-cols-2 gap-5" data-testid="comp-form">
      <F label="Title"><input data-testid="comp-title" className={inp} value={f.title} onChange={set("title")} maxLength={60} /></F>
      <F label={`Reward pot (${meta?.symbol || "token"})`}><input data-testid="comp-reward" className={inp} inputMode="decimal" placeholder="e.g. 1000000" value={f.reward} onChange={set("reward")} /></F>
      <F label="Starts"><input data-testid="comp-start" type="datetime-local" className={inp} value={f.start} onChange={set("start")} /></F>
      <F label="Ends"><input data-testid="comp-end" type="datetime-local" className={inp} value={f.end} onChange={set("end")} /></F>
      <F label="Number of winners">
        <select data-testid="comp-winners-select" className={inp} value={f.winners} onChange={(e) => setWinners(Number(e.target.value))}>
          {Array.from({ length: 10 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
        </select>
      </F>
      <F label="Minimum volume to qualify (USD)"><input data-testid="comp-min-volume" className={inp} inputMode="decimal" value={f.min_volume_usd} onChange={set("min_volume_usd")} /></F>
      <div className="md:col-span-2 space-y-1.5"><span className="text-xs text-slate-400">Rank traders by</span>
        <Choice testid="comp-metric" value={f.metric} set={set("metric")} options={[["buy_volume", "Buy volume", "only buys count"], ["total_volume", "Buy + sell volume", "every trade counts"], ["net_buys", "Net buys", "buys minus sells"]]} />
      </div>
      <div className="md:col-span-2 space-y-2"><span className="text-xs text-slate-400">Split the pot</span>
        <Choice testid="comp-split" value={f.split_mode} set={set("split_mode")} options={[["top_heavy", "Top-heavy", "bigger share for 1st"], ["equal", "Equal", "same for every winner"], ["custom", "Custom %", "you set each place"]]} />
        {f.split_mode === "custom" && (
          <div className="flex flex-wrap gap-2 items-center" data-testid="comp-custom-pcts">
            {f.pcts.map((p, i) => <label key={i} className="flex items-center gap-1 text-xs text-slate-400">#{i + 1}<input data-testid={`comp-pct-${i + 1}`} className="w-16 rounded border border-white/10 bg-black/40 px-2 py-1 text-white" value={p} onChange={(e) => setF((x) => ({ ...x, pcts: x.pcts.map((q, j) => (j === i ? e.target.value : q)) }))} />%</label>)}
            <span className={`text-xs ${Math.abs(sum - 100) < 0.001 ? "text-lime" : "text-danger"}`} data-testid="comp-pct-sum">= {sum.toFixed(2)}%</span>
          </div>
        )}
      </div>
      <div className="md:col-span-2 space-y-1.5"><span className="text-xs text-slate-400">If fewer traders qualify than winner places</span>
        <Choice testid="comp-leftover" value={f.leftover} set={set("leftover")} options={[["refund", "Unused share returns to me"], ["redistribute", "Share it among the winners"]]} />
      </div>
      <div className="md:col-span-2 flex flex-wrap items-center gap-3">
        <button data-testid="comp-create-submit" disabled={busy} onClick={submit} className="btn-lime px-6 py-2.5 text-sm disabled:opacity-40">{busy ? "Creating…" : "Create competition"}</button>
        <p className="text-[11px] text-slate-500">Next you deploy a pot contract from your wallet and send the reward into it. The pot pays the winners automatically from the HIVEVORA-signed results — you can't withdraw it early.</p>
      </div>
    </div>
  );
};
